import React from 'react';

import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { type NonPhysicalLimitation } from 'src/services/jmr/types';
import { setSelectedLimitations } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrFlowTypes } from 'src/utils/jmrUtils';
import { type LimitationConfig } from 'src/utils/restrictionUtils';

import RestrictionItemBox from '../restrictionPageNewExperience/restrictionItemBox';

import DurationSelector from './durationSelector';
import FreeFormSelector from './freeFormSelector';
import FrequencySelector from './frequencySelector';

// **** Constants **** //

// Exporting for testing
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

/**
 * Common props for every restriction selector. `restrictionItem` is generic so each selector
 * receives the concrete restriction type it renders (e.g. YesNoRestrictionTask).
 */
interface SelectorProps {
  limitationItem: LimitationConfig;
  isDisabled: boolean;
  index: number;
  updateLimitationOption: (arg: LimitationConfig) => void;
}
/**
 * Main component.
 */
const NonPhysicalLimitations = () => {
  const dispatch = useAppDispatch();
  const { selectedLimitations } = useAppSelector((state) => state.newJmrPage);
  const { jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const isDisabled = jmrFlowType === JmrFlowTypes.UpdateDates;

  const updateLimitationOption = (updatedLimitation: NonPhysicalLimitation) =>
    dispatch(
      setSelectedLimitations(
        selectedLimitations.map((original) =>
          original.nonPhysicalLimitationTaskId === updatedLimitation.nonPhysicalLimitationTaskId
            ? updatedLimitation
            : original,
        ),
      ),
    );

  return (
    <Column spacing="400" spacingInset="400">
      {selectedLimitations.length ? (
        <Heading alignment="center" className="newJmr__restrictionPage__heading" level={4}>
          Non Physical Limitations
        </Heading>
      ) : null}
      {selectedLimitations.map((item, index) => {
        const selectorProps = { isDisabled, index, updateLimitationOption };

        return (
          <RestrictionItemBox
            restrictionNumber={index}
            key={item.nonPhysicalLimitationTaskId}
            label={item.nonPhysicalLimitationTask.name}
            description={item.nonPhysicalLimitationTask.description}
            onRemoveRestriction={() =>
              dispatch(
                setSelectedLimitations(
                  selectedLimitations.filter(
                    ({ nonPhysicalLimitationTaskId }) =>
                      nonPhysicalLimitationTaskId !== item.nonPhysicalLimitationTaskId,
                  ),
                ),
              )
            }
          >
            {(item.nonPhysicalLimitationTask.allowsFreeFormText ||
              item.nonPhysicalLimitationTask.allowsDuration ||
              item.nonPhysicalLimitationTask.allowsFrequency) && (
              <Column>
                {item.nonPhysicalLimitationTask.allowsFreeFormText && (
                  <Row widths="fill">
                    <FreeFormLimitation limitationItem={item} {...selectorProps} />
                  </Row>
                )}

                {(item.nonPhysicalLimitationTask.allowsDuration || item.nonPhysicalLimitationTask.allowsFrequency) && (
                  <Row widths="fill" alignmentVertical="top">
                    {item.nonPhysicalLimitationTask.allowsDuration && (
                      <DurationLimitation limitationItem={item} {...selectorProps} />
                    )}
                    {item.nonPhysicalLimitationTask.allowsFrequency && (
                      <FrequencyLimitation limitationItem={item} {...selectorProps} />
                    )}
                  </Row>
                )}
              </Column>
            )}
          </RestrictionItemBox>
        );
      })}
    </Column>
  );
};

const FrequencyLimitation = ({ limitationItem, isDisabled, index, updateLimitationOption }: SelectorProps) => {
  const onChange = (newValue: number) => {
    if (+newValue !== limitationItem.frequency) {
      const { frequencyError, ...toUpdate } = limitationItem;
      toUpdate.frequency = +newValue;
      updateLimitationOption(toUpdate);
    }
  };

  return (
    <FrequencySelector
      selectedValue={limitationItem.frequency}
      onSelectValue={onChange}
      isDisabled={isDisabled}
      errorMessage={limitationItem.frequencyError}
      index={index}
    />
  );
};

const DurationLimitation = ({ limitationItem, isDisabled, index, updateLimitationOption }: SelectorProps) => {
  const onChange = (newValue: number) => {
    if (+newValue !== limitationItem.duration) {
      const { durationError, ...toUpdate } = limitationItem;
      toUpdate.duration = +newValue;
      updateLimitationOption(toUpdate);
    }
  };

  return (
    <DurationSelector
      value={limitationItem.duration}
      onChange={onChange}
      isDisabled={isDisabled}
      errorMessage={limitationItem.durationError}
      index={index}
    />
  );
};

const FreeFormLimitation = ({ limitationItem, isDisabled, index, updateLimitationOption }: SelectorProps) => {
  const onChange = (newValue: string) => {
    if (newValue !== limitationItem.freeFormText) {
      const { freeFormError, ...toUpdate } = limitationItem;
      toUpdate.freeFormText = newValue;
      updateLimitationOption(toUpdate);
    }
  };

  return (
    <FreeFormSelector
      value={limitationItem.freeFormText || ''}
      onChange={onChange}
      isDisabled={isDisabled}
      errorMessage={limitationItem.freeFormError}
      index={index}
    />
  );
};

export default NonPhysicalLimitations;
